import React from "react";
import ReactDOM from "react-dom";
import { optional, parseBoolean, walk } from "../../../utils/lang";
import { RATING_TYPE } from "../../../model/vars";
import _ from "underscore";
import * as SurveysUtils from "../../utils/surveys";
import { isEmpty, isNotEmpty, getImageServerUrl } from "../../../framework/utils";
import Actions, { ACTION_TYPES } from "./actions";
import { hideDialog, showDialog, updateDialog } from "../../../actions/dialog";
import M from "../../../strings";
import { MobileInput, MobileTextArea } from "./forms";
import { MaterialIcon } from "../materialIcon";
import { connect } from "../../../utils/aj-react";
import { getDefaultState, SurveysStore } from "../../../stores/surveys";
import { mobileConfirm } from "../../../plugins";
import GradedValue, { GRADE_TYPES } from "./gradedValue";
import PictureUploader from "./pictureUpload";
import { DROPDOWN_ALIGNMENT } from "./dropdown";

const QuestionIdContext = React.createContext();

export default class QuestionControl extends React.Component {

    onChange(value) {
        const questionData = this.props.questionData,
            question = questionData.question,
            canChange = this.canChange();

        if (canChange && this.props.onChange && _.isFunction(this.props.onChange)) {
            this.props.onChange({
                input: value,
                questionId: question.id,
            })
        }
    }

    onSaveNote(value) {
        const questionData = this.props.questionData,
            question = questionData.question,
            disabled = this.isDisabled();

        if (!disabled && this.props.onSaveNote && _.isFunction(this.props.onSaveNote)) {
            this.props.onSaveNote({
                value: value,
                questionId: question.id,
            })
        }
    }

    onSavePictures(pictures) {
        const questionData = this.props.questionData,
            question = questionData.question,
            rating = question.rating,
            disabled = this.isDisabled();

        if (!disabled && this.props.onSavePictures && _.isFunction(this.props.onSavePictures)) {
            this.props.onSavePictures({
                pictures: pictures,
                questionId: question.id,
                ratingId: rating?.id ?? undefined,
            })
        }
    }

    onDeletePictures(pictures) {
        const questionData = this.props.questionData,
            question = questionData.question,
            rating = question.rating,
            disabled = this.isDisabled();

        if (!disabled && this.props.onDeletePictures && _.isFunction(this.props.onDeletePictures)) {
            this.props.onDeletePictures({
                pictures: pictures,
                questionId: question.id,
                ratingId: rating?.id ?? undefined,
            })
        }
    }

    showPicture(uuid) {
        const disabled = this.isDisabled(),
            questionData = optional(this.props.questionData),
            question =  questionData.question;

        if (disabled) {
            return false;
        }

        showDialog({
            mobile: true,
            //title: M("addPicturesDialogTitle"),
            //subtitle: M("addPicturesDialogSubtitle"),
            getContent: () => (<EditPicturesPopup
                    questionId={question.id}
                    selection={uuid}
                    onSave={this.onSavePictures.bind(this)}
                    onDelete={this.onDeletePictures.bind(this)}
                />),
            leftHeaderActions: [
                    {
                        id: "close",
                        type: ACTION_TYPES.ICON,
                        icon: "close",
                        className: "hidden-sm-up",
                        onClick: hideDialog,
                    }
                ],
        })
    }
    
    getInputComponent() {
        const questionData = optional(this.props.questionData),
            question = optional(questionData.question, {}),
            template = optional(question.template, {}),
            ratingType = template.ratingType;

        switch(ratingType) {
            case RATING_TYPE.ONE_TO_FIVE:
                return OneToFive;
            case RATING_TYPE.ONE_OR_FIVE:
                return OneOrFive;
            case RATING_TYPE.TEXT:
                return Text;
            case RATING_TYPE.TARGET:
                return Target;
        }
    }

    isDisabled() {
        return parseBoolean(optional(this.props.disabled, false));
    }

    isReadonly() {
        const questionData = this.props.questionData,
            question = questionData.question,
            template = question.template,
            readOnly = parseBoolean(optional(template.readOnly, false));

        return readOnly;
    }

    canChange() {
        return !this.isReadonly() && !this.isDisabled();
    }

    render() {
        const disabled = this.isDisabled(),
            questionData = optional(this.props.questionData),
            question =  questionData.question,
            relatedQuestions = optional(questionData.relatedQuestions, []),
            template = optional(question.template, {}),
            rating = optional(question.rating, {}),
            notes = rating.notes,
            photos = optional(rating.photos, []),
            value = SurveysUtils.getRatingValue(rating, template?.readOnly ?? false),
            InputComponent = this.getInputComponent(),
            actions = [];

        if (template.allowNotes) {
            actions.push({
                id: "notes",
                type: ACTION_TYPES.ICON,
                icon: "notes",
                rounded: true,
                onClick: () => {
                        if (disabled) {
                            return false;
                        }
                        showDialog({
                            mobile: true,
                            getContent: () => <AddNotePopup
                                    value={notes}
                                    onSave={this.onSaveNote.bind(this)}
                                />
                        })
                    },
            })
        }

        if (template.allowPhotos) {
            actions.push({
                id: "pictures",
                type: ACTION_TYPES.CUSTOM,
                component: PictureUploader,
                rounded: true,
                onImagesAdded: this.onSavePictures.bind(this),
            })
        }

        return(
            <QuestionIdContext.Provider value={question.id}>
                <div
                    className="question-control"
                    data-id={question.id}
                >
                    <div
                        className="question-control__description"
                    >
                        {template.description}
                    </div>
                    <div
                        className="question-control__input-group"
                    >
                        <div
                            className={"question-control__input" + (disabled ? " question-control__input--disabled" : "")}
                        >
                            {InputComponent && <InputComponent
                                value={value}
                                disabled={!this.canChange()}
                                onChange={this.onChange.bind(this)}
                            />}
                        </div>
                        {!disabled && <Actions
                            className="question-control__actions"
                            actions={actions}
                        />}
                    </div>
                    {isNotEmpty(photos) && <div
                            className="question-control__pictures"
                        >
                            {_.map(photos, p => {
                                const {uuid, path} = p,
                                    url = getImageServerUrl(path, "96x96");

                                return <div
                                        key={"question-control-picture-" + uuid}
                                        style={{backgroundImage: 'url(' + url + ')'}}
                                        className="question-control__picture"
                                        onClick={this.showPicture.bind(this,uuid)}
                                    />
                            })}
                        </div>}
                    {isNotEmpty(notes) && <div
                        className="question-control__notes"
                    >
                        {notes}
                    </div>}
                    {isNotEmpty(relatedQuestions) && (
                        <div
                            className="question-control__related-questions"
                        >
                            {_.map(relatedQuestions, relatedQuestion => {
                                return (
                                    <QuestionControl
                                        key={question.id + "-" + relatedQuestion.id}
                                        questionData = {{question: relatedQuestion}}
                                        onChange={this.props.onChange}
                                        onSaveNote={this.props.onSaveNote}
                                        disabled={disabled}
                                    />
                                )
                            })}
                        </div>
                    )}
                </div>
            </QuestionIdContext.Provider>
        )
    }
}

class OneToFive extends React.Component {
    render() {
        return (
            <QuestionSelect
                {...this.props}
                options = {[1, 2, 3, 4, 5]}
            />
        )
    }
}

class OneOrFive extends React.Component {
    render() {
        return (
            <QuestionSelect
                {...this.props}
                options = {[1, 5]}
            />
        )
    }
}

class QuestionSelect extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: this.sanitizeValue(this.props.value),
            parentValue: this.sanitizeValue(this.props.value),
        }
    }

    componentDidMount() {
        this.syncValue();
    }

    componentDidUpdate() {
        this.syncValue();
    }

    sanitizeValue(value) {
        const _value = isNotEmpty(value) ? parseInt(value) : undefined;

        return _.isNumber(_value) ? _value : undefined;
    }

    syncValue() {
        const newValue = this.sanitizeValue(this.props.value),
            oldValue = this.sanitizeValue(this.state.parentValue);

        if (newValue !== oldValue) {
            this.setState({
                value: newValue,
                parentValue: newValue,
            })
        }
    }

    onChange(_value) {

        if (this.isDisabled()) {
            return false;
        }

        const oldValue = this.state.value?.toString() ?? "",
            newValue = _value?.toString() ?? "",
            shouldDeselect = newValue === oldValue,
            value = shouldDeselect ? "" : _value;

        this.setState({value: value}, () => {
            if (this.props.onChange && _.isFunction(this.props.onChange)) {
                this.props.onChange(value);
            }
        })
    }

    isDisabled() {
        return parseBoolean(optional(this.props.disabled, false));
    }

    render() {
        const disabled = this.isDisabled(),
            value = this.state.value,
            options = optional(this.props.options, []);

        return (
            <div
                className={"question-control__select" + (disabled ? " question-control__select--disabled" : "")}
            >
                {_.map(options, option => {
                    const isSelected = option == value,
                        className = isSelected ? "question-control__select-option question-control__select-option--selected" : "question-control__select-option";

                    return <GradedValue
                        key={"question-" + QuestionIdContext + "_option-" + option}
                        className={className}
                        onClick={this.onChange.bind(this, option)}
                        value={option}
                        gradeType={GRADE_TYPES.RATING}
                    />
                })}
                {disabled && <MaterialIcon
                    className="ml-3"
                    icon="lock"
                />}
            </div>
        )
    }
}

class Text extends React.Component {
    onBlur(e) {
        const value = e.target.value,
            oldValue = this.props.value?.toString() ?? "";
        if (this.props.onChange && _.isFunction(this.props.onChange) && oldValue !== value) {
            this.props.onChange(value);
        }
    }

    render() {
        return <MobileInput
                {..._.omit(this.props,"onChange")}
                type="text"
                onBlur={this.onBlur.bind(this)}
                value={this.props.value}
            />
    }
}

class Target extends React.Component {
    onBlur(e) {
        const value = e.target.value,
            oldValue = this.props.value?.toString() ?? "";

        if (this.props.onChange && _.isFunction(this.props.onChange) && oldValue !== value) {
            this.props.onChange(value);
        }
    }

    render() {
        return <MobileInput
                {..._.omit(this.props,"onChange")}
                type="number"
                onBlur={this.onBlur.bind(this)}
                value={this.props.value}
            />
    }
}

class AddNotePopup extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: this.props.value,
        }
    }

    componentDidMount() {
        updateDialog({
            title: M("newNoteDialogTitle"),
            subtitle: M("newNoteDialogSubtitle"),
            leftHeaderActions: [
                    {
                        id: "close",
                        type: ACTION_TYPES.ICON,
                        icon: "close",
                        className: "hidden-sm-up",
                        onClick: hideDialog,
                    }
                ],
            rightHeaderActions: [
                    {
                        id: "save",
                        type: ACTION_TYPES.BUTTON,
                        text: M("save"),
                        className: "btn-transparent-primary",
                        onClick: this.onSave.bind(this),
                    }
                ],
        })
    }

    onChange(e) {
        const value = e.target.value;
        this.setState({value: value});
    }

    onSave() {
        const value = this.state.value;
        if (this.props.onSave && _.isFunction(this.props.onSave) && this.props.value !== this.state.value) {
            this.props.onSave(value);
        }
        hideDialog();
    }

    render() {
        return(
            <div
                className="p-3"
            >
                <MobileTextArea
                    label={M("note")}
                    placeholder={M("note")}
                    outlined={true}
                    value={this.state.value}
                    onChange={this.onChange.bind(this)}
                />
            </div>
        )
    }
}

class EditPicturesPopup extends React.Component {

    constructor(props) {
        super(props);
        
        connect(this, SurveysStore, getDefaultState());

        this.state = {
            selection: this.props.selection,
        }
    }

    componentDidMount() {
        updateDialog({
            rightHeaderActions: [
                {
                    id: "pictures",
                    type: ACTION_TYPES.CUSTOM,
                    component: PictureUploader,
                    rounded: true,
                    onImagesAdded: this.onImagesAdded.bind(this),
                    dropdownOptions: {
                        alignment: DROPDOWN_ALIGNMENT.RIGHT,
                    },
                },
            ],
        })
    }

    getQuestion() {
        const questionId = this.props.questionId,
            currentSurvey = optional(this.state.currentSurvey, {}),
            roots = optional(currentSurvey.roots, []);

        let result;

        if (isEmpty(currentSurvey)) {
            return undefined;
        }

        walk(roots, "children", c => {
            const questions = optional(c.questions, []);
    
            _.each(questions, q => {
                const question = q.question,
                    relatedQuestions = optional(q.relatedQuestions, []);

                if (question.id === questionId) {
                    result = question;
                }
    
                _.each(relatedQuestions, rquestion => {
                    if (rquestion.id === questionId) {
                        result = rquestion;
                    }
                })
            })
    
            
        });

        return result;
    }

    getImages() {
        const question = this.getQuestion();

        if (isEmpty(question)) {
            return [];
        }

        const rating = optional(question.rating, {}),
            photos = optional(rating.photos, []);

        return photos;
    }

    onImagesAdded(images) {
        if (this.props.onSave && _.isFunction(this.props.onSave)) {
            this.props.onSave(images);
        }
    }

    removeImage(image, e) {
        e && e.preventDefault() && e.stopPropagation();

        mobileConfirm(M("deleteRatingPhotoConfirmTitle"), M("deleteRatingPhotoConfirmMessage"))
            .then(() => {
                if (this.props.onDelete && _.isFunction(this.props.onDelete)) {
                    this.props.onDelete([image]);
                }
            })
    }

    select(uuid, e) {
        e && e.preventDefault() && e.stopPropagation();

        if (e.target != e.currentTarget) {
            return false;
        }

        this.setState({selection: uuid})
    }

    unselect() {
        this.setState({selection: undefined})
    }

    render() {
        const images = this.getImages(),
            selectionUuid = this.state.selection,
            selection = _.findWhere(images, {uuid: selectionUuid});

        return(
            <div
                className="p-3"
            >
                {isNotEmpty(selection) && <div
                    className="question-pictures__selection-wrapper"
                >
                    <div
                        className="question-pictures__selection"
                    >
                        <img
                            className="question-pictures__selection-image img-fluid"
                            src={getImageServerUrl(selection.path)}
                            alt={M("image")}
                        />
                        <div
                            className="question-pictures__selection-actions"
                        >
                            <MaterialIcon
                                icon="delete"
                                onClick={this.removeImage.bind(this,selection)}
                            />
                        </div>
                    </div>
                </div>}
                {isNotEmpty(images) ? (
                    <div
                        className="question-pictures__items"
                    >
                        {_.map(images, image => {
                            const url = getImageServerUrl(image.path, "256x256"),
                                uuid = image.uuid,
                                isSelected = uuid === selectionUuid;

                            return (
                                <div
                                    style={{backgroundImage: 'url(' + url + ')'}}
                                    key={"question-pictures__item-" + uuid}
                                    className={"question-pictures__item" + (isSelected ? " question-pictures__item--selected" : "")}
                                >
                                    <div
                                        className="question-pictures__item-actions"
                                        onClick={this.select.bind(this, uuid)}
                                    >
                                        <MaterialIcon
                                            icon="delete"
                                            onClick={this.removeImage.bind(this,image)}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div
                        className="m5 text-center"
                    >
                        {M("addPicturesDialogNoPictures")}
                    </div>
                )}
            </div>
        )
    }
}